import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Typography,
} from "@mui/material";
import { ArrowBack, ArrowForward, RocketLaunch } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import {
  ApiIncrementalityControlGroupType,
  ApiIncrementalityTest,
  ApiIncrementalityTestPayload,
  ApiIncrementalityTestStatus,
  ApiIncrementalityTestType,
  ApiMetric,
} from "@incendium/api";
import { StyledStepper } from "Components/UI/StyledStepper";
import GlassCard from "Components/GlassCard/GlassCard";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import produce from "immer";
import IncrementalityTestRegionMap from "./IncrementalityTestRegionMap";
import IncrementalityTestRegionAutocomplete from "./IncrementalityTestRegionAutocomplete";
import IncrementalityControlGroupPreview from "./IncrementalityControlGroupPreview";
import IncrementalityTestSummary from "./IncrementalityTestSummary";
import IncrementalityMetricsTab from "./IncrementalityMetricsTab";
import IncrementalityReviewTab from "./IncrementalityReviewTab";
import IncrementalityResultsTab from "./IncrementalityResultsTab";
import IncrementalitySetupProvider, {
  useIncrementalitySetupContext,
} from "../providers/IncrementalitySetupProvider";
import {
  PeriodDescription,
  SectionSubTitle,
  SectionTitle,
  SummaryWrap,
  TestTypeCard,
} from "./styled";
import {
  createIncrementalityTest,
  readIncrementalityTest,
  updateIncrementalityTest,
} from "../services/incrementalityService";
import { useHistory, useParams } from "react-router-dom";
import { useNotification } from "Hooks";
import useAppendSearchState from "Hooks/useAppendSearchState";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";
import moment from "moment";
import { useSelectedProject } from "Hooks/useSelectedProject";
import { useIncrementalityTests } from "../hooks/useIncrementalityTests";
import {
  AUTO_MATCHED_REGIONS_RECOMMENDED_LABEL,
  buildAutoMatchedControlDescription,
  buildCustomRegionsControlDescription,
  buildRestOfCountryControlDescription,
  buildSetupHoldoutNotice,
  COMING_SOON_LABEL,
  CUSTOM_REGIONS_LABEL,
  CONTROL_GROUP_LABEL,
  CONTROL_PERIOD_DESCRIPTION,
  CONTROL_PERIOD_LABEL,
  DATES_SECTION_SUBTITLE,
  DATES_SECTION_TITLE,
  GEO_ROLLOUT_DESCRIPTION,
  GEO_ROLLOUT_LABEL,
  GEO_SWITCH_OFF_DESCRIPTION,
  GEO_SWITCH_OFF_LABEL,
  getDateValidationMessages,
  NOTIFICATION_MESSAGES,
  RECOMMENDED_CHIP_LABEL,
  REGION_SECTION_SUBTITLE,
  REGION_SECTION_TITLE,
  SELECTED_CHIP_LABEL,
  SETUP_STEP_LABELS,
  TEST_NAME_PLACEHOLDER,
  TEST_NAME_REQUIRED_ERROR,
  TEST_PERIOD_DESCRIPTION,
  TEST_PERIOD_LABEL,
  TEST_TYPE_SECTION_SUBTITLE,
  TEST_TYPE_SECTION_TITLE,
  VALIDATION_MESSAGES,
} from "../helpers/copy";
import { incrementalityTestSnapshot } from "../helpers/incrementalityTestSnapshot";

const SETUP_STEPS = ["design", "metrics", "review", "results"] as const;
type SetupStep = (typeof SETUP_STEPS)[number];

const isSetupStep = (value: string | null): value is SetupStep =>
  value !== null && (SETUP_STEPS as readonly string[]).includes(value);

function IncrementalitySetupTabContent() {
  const { setIncrementalityTest, incrementalityTest, setTrendScore } =
    useIncrementalitySetupContext();
  const [step, setStep] = useAppendSearchState<SetupStep>("step", "design", {
    deserialize: (value) => (isSetupStep(value) ? value : "design"),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const { testId } = useParams<{ testId: string }>();
  const history = useHistory();
  const { pathByName } = useProtectedRoutes();
  const { selectedClient, selectedProject } = useSelectedProject();
  const { setTests } = useIncrementalityTests();
  const { showErrorNotification, showSuccessNotification } = useNotification();

  const numericTestId = useMemo(() => {
    if (!testId || testId === "new") return undefined;
    const parsed = Number(testId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [testId]);
  const hasResultsTab = !!numericTestId;

  const activeStep = useMemo(
    () => Math.max(0, SETUP_STEPS.indexOf(step ?? "design")),
    [step],
  );

  const setActiveStep = useCallback(
    (index: number) => {
      const clamped = Math.min(
        Math.max(index, 0),
        hasResultsTab ? SETUP_STEPS.length - 1 : 2,
      );
      const next = SETUP_STEPS[clamped];
      if (next) setStep(next);
    },
    [hasResultsTab, setStep],
  );

  useEffect(() => {
    if (!hasResultsTab && step === "results") {
      setStep("review");
    }
  }, [hasResultsTab, step, setStep]);

  useEffect(() => {
    if (!numericTestId) {
      setSavedSnapshot(null);
    }
  }, [numericTestId]);

  const hasUnsavedChanges = useMemo(() => {
    if (!numericTestId || savedSnapshot === null) return false;
    return (
      incrementalityTestSnapshot(incrementalityTest) !== savedSnapshot
    );
  }, [incrementalityTest, numericTestId, savedSnapshot]);

  const showCreateButton = !numericTestId;
  const showUpdateButton = !!numericTestId && hasUnsavedChanges;

  const dateValidationMessages = useMemo(
    () =>
      getDateValidationMessages({
        testFrom: incrementalityTest.testFrom
          ? moment(incrementalityTest.testFrom)
          : null,
        testTo: incrementalityTest.testTo
          ? moment(incrementalityTest.testTo)
          : null,
        preTestFrom: incrementalityTest.preTestFrom
          ? moment(incrementalityTest.preTestFrom)
          : null,
        preTestTo: incrementalityTest.preTestTo
          ? moment(incrementalityTest.preTestTo)
          : null,
      }),
    [
      incrementalityTest.preTestFrom,
      incrementalityTest.preTestTo,
      incrementalityTest.testFrom,
      incrementalityTest.testTo,
    ],
  );

  const holdoutNotice = useMemo(
    () => buildSetupHoldoutNotice(incrementalityTest),
    [
      incrementalityTest.testRegionCity,
      incrementalityTest.testRegionCountry,
      incrementalityTest.testFrom,
      incrementalityTest.testTo,
      incrementalityTest.type,
    ],
  );

  useEffect(() => {
    if (!selectedProject?.id || !numericTestId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        const test = await readIncrementalityTest(
          selectedProject.id as number,
          numericTestId,
        );
        if (cancelled) return;
        const loadedTest: ApiIncrementalityTestPayload = {
          name: test.name || "",
          type: test.type,
          testRegionCity: test.testRegionCity,
          testRegionCountry: test.testRegionCountry,
          testRegionLat: test.testRegionLat,
          testRegionLon: test.testRegionLon,
          controlGroupType:
            test.controlGroupType ||
            ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED,
          testFrom: test.testFrom ? moment(test.testFrom).toDate() : undefined,
          testTo: test.testTo ? moment(test.testTo).toDate() : undefined,
          preTestFrom: test.preTestFrom
            ? moment(test.preTestFrom).toDate()
            : undefined,
          preTestTo: test.preTestTo ? moment(test.preTestTo).toDate() : undefined,
          primaryMetric:
            (test.primaryMetric as ApiMetric) ||
            ApiMetric.METRIC_REVENUE_PER_SESSION,
          supportingMetrics: (test.supportingMetrics || [])
            .map((metric) => metric.metric as ApiMetric)
            .filter(Boolean),
          controlRegions:
            (test.controlGroupType ||
              ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED) ===
            ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED
              ? test.controlRegions || []
              : [],
          baselineGeneratedAt: test.baselineGeneratedAt,
        };
        setIncrementalityTest(loadedTest);
        setSavedSnapshot(incrementalityTestSnapshot(loadedTest));
      } catch (error) {
        showErrorNotification(NOTIFICATION_MESSAGES.loadFailed);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    selectedProject?.id,
    numericTestId,
    setIncrementalityTest,
    showErrorNotification,
  ]);

  const onSave = async () => {
    if (!selectedProject?.id) return;
    if (!incrementalityTest.name?.trim()) {
      showErrorNotification(VALIDATION_MESSAGES.testName);
      return;
    }
    if (
      !incrementalityTest.testRegionCity ||
      !incrementalityTest.testRegionCountry
    ) {
      showErrorNotification(VALIDATION_MESSAGES.testRegion);
      return;
    }
    if (!incrementalityTest.testFrom || !incrementalityTest.testTo) {
      showErrorNotification(VALIDATION_MESSAGES.testDates);
      return;
    }
    if (!incrementalityTest.preTestFrom || !incrementalityTest.preTestTo) {
      showErrorNotification(VALIDATION_MESSAGES.controlPeriodDates);
      return;
    }
    if (dateValidationMessages.length) {
      showErrorNotification(dateValidationMessages[0]);
      return;
    }
    if (!incrementalityTest.primaryMetric) {
      showErrorNotification(VALIDATION_MESSAGES.primaryMetric);
      return;
    }
    if (!incrementalityTest.supportingMetrics?.length) {
      showErrorNotification(VALIDATION_MESSAGES.supportingMetrics);
      return;
    }

    const payload = {
      ...incrementalityTest,
      name: incrementalityTest.name?.trim(),
      status: ApiIncrementalityTestStatus.INCREMENTALITY_TEST_STATUS_ACTIVE,
    };

    try {
      setIsSaving(true);
      let res: ApiIncrementalityTest | null = null;
      if (numericTestId) {
        res = await updateIncrementalityTest(
          selectedProject.id as number,
          numericTestId,
          payload,
        );
        showSuccessNotification(NOTIFICATION_MESSAGES.updated);
      } else {
        res = await createIncrementalityTest(
          selectedProject.id as number,
          payload,
        );

        showSuccessNotification(NOTIFICATION_MESSAGES.created);
      }

      if (!res) return;

      setTests((prev) =>
        produce(prev || [], (draft) => {
          const idx = draft.findIndex((item) => item.id === res!.id);
          if (idx >= 0) {
            draft[idx] = res!;
          } else {
            draft.push(res!);
          }
        }),
      );

      const savedPayload: ApiIncrementalityTestPayload = {
        ...incrementalityTest,
        name: incrementalityTest.name?.trim(),
        controlRegions:
          incrementalityTest.controlGroupType ===
          ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED
            ? incrementalityTest.controlRegions || []
            : [],
      };
      setSavedSnapshot(incrementalityTestSnapshot(savedPayload));

      if (res.id) {
        if (!numericTestId) {
          history.replace({
            pathname: pathByName(RouteNames.INCREMENTALITY_SETUP, {
              clientId: selectedClient?.id,
              projectId: selectedProject?.id,
              testId: res.id,
            }),
            search: "?step=results",
          });
        }
        setStep("results");
      }
    } catch (error) {
      showErrorNotification(NOTIFICATION_MESSAGES.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ValidatorForm onSubmit={onSave}>
      <Box mt={2} sx={{ width: { xs: "100%", md: 860 }, mb: 3 }}>
        <StyledStepper activeStep={activeStep}>
          <Step completed={activeStep > 0}>
            <StepLabel
              onClick={() => setActiveStep(0)}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="subtitle2">
                {SETUP_STEP_LABELS.design.title}
              </Typography>
              <Typography variant="body2">
                {SETUP_STEP_LABELS.design.subtitle}
              </Typography>
            </StepLabel>
          </Step>
          <Step completed={activeStep > 1}>
            <StepLabel
              onClick={() => setActiveStep(1)}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="subtitle2">
                {SETUP_STEP_LABELS.metrics.title}
              </Typography>
              <Typography variant="body2">
                {SETUP_STEP_LABELS.metrics.subtitle}
              </Typography>
            </StepLabel>
          </Step>
          <Step>
            <StepLabel
              onClick={() => setActiveStep(2)}
              sx={{ cursor: "pointer" }}
            >
              <Typography variant="subtitle2">
                {SETUP_STEP_LABELS.review.title}
              </Typography>
              <Typography variant="body2">
                {SETUP_STEP_LABELS.review.subtitle}
              </Typography>
            </StepLabel>
          </Step>
          {hasResultsTab && (
            <Step>
              <StepLabel
                onClick={() => setActiveStep(3)}
                sx={{ cursor: "pointer" }}
              >
                <Typography variant="subtitle2">
                  {SETUP_STEP_LABELS.results.title}
                </Typography>
                <Typography variant="body2">
                  {SETUP_STEP_LABELS.results.subtitle}
                </Typography>
              </StepLabel>
            </Step>
          )}
        </StyledStepper>
      </Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8.5}>
          {activeStep === 0 ? (
            <Stack spacing={2.5}>
              <GlassCard boxProps={{ p: 3 }}>
                <Stack spacing={2}>
                  <Box>
                    <SectionTitle>{TEST_TYPE_SECTION_TITLE}</SectionTitle>
                    <SectionSubTitle>
                      {TEST_TYPE_SECTION_SUBTITLE}
                    </SectionSubTitle>
                  </Box>
                  <TextValidator
                    name="incrementality-test-name"
                    fullWidth
                    label="Test name"
                    value={incrementalityTest.name || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setIncrementalityTest((prev) =>
                        produce(prev, (draft) => {
                          draft.name = e.target.value;
                        }),
                      )
                    }
                    placeholder={TEST_NAME_PLACEHOLDER}
                    validators={["required"]}
                    errorMessages={[TEST_NAME_REQUIRED_ERROR]}
                  />
                  <Stack spacing={2} direction="row">
                    <TestTypeCard
                      $selected={
                        incrementalityTest.type ===
                        ApiIncrementalityTestType.INCREMENTALITY_TEST_TYPE_GEO_SWITCH_OFF
                      }
                      onClick={() =>
                        setIncrementalityTest((prev) =>
                          produce(prev, (draft) => {
                            draft.type =
                              ApiIncrementalityTestType.INCREMENTALITY_TEST_TYPE_GEO_SWITCH_OFF;
                          }),
                        )
                      }
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            {GEO_SWITCH_OFF_LABEL}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {GEO_SWITCH_OFF_DESCRIPTION}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color="primary"
                          label={SELECTED_CHIP_LABEL}
                        />
                      </Stack>
                      <Chip
                        sx={{ mt: 1.5 }}
                        size="small"
                        label={RECOMMENDED_CHIP_LABEL}
                        color="success"
                      />
                    </TestTypeCard>
                    <TestTypeCard $disabled>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            {GEO_ROLLOUT_LABEL}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {GEO_ROLLOUT_DESCRIPTION}
                          </Typography>
                        </Box>
                        <Chip size="small" label={COMING_SOON_LABEL} />
                      </Stack>
                    </TestTypeCard>
                  </Stack>
                </Stack>
              </GlassCard>

              <GlassCard boxProps={{ p: 3 }}>
                <Stack>
                  <Box>
                    <SectionTitle>{DATES_SECTION_TITLE}</SectionTitle>
                    <SectionSubTitle>{DATES_SECTION_SUBTITLE}</SectionSubTitle>
                  </Box>
                  <Grid mt={2} container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">
                          {TEST_PERIOD_LABEL}
                        </Typography>
                        <PeriodDescription>
                          {TEST_PERIOD_DESCRIPTION}
                        </PeriodDescription>
                        <DatePicker
                          label="From"
                          value={
                            incrementalityTest.testFrom
                              ? moment(incrementalityTest.testFrom)
                              : null
                          }
                          onChange={(value) =>
                            setIncrementalityTest((prev) =>
                              produce(prev, (draft) => {
                                draft.testFrom = value
                                  ? moment(value as any).toDate()
                                  : undefined;
                              }),
                            )
                          }
                          maxDate={
                            incrementalityTest.testTo
                              ? moment(incrementalityTest.testTo)
                              : undefined
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                        <DatePicker
                          label="To"
                          value={
                            incrementalityTest.testTo
                              ? moment(incrementalityTest.testTo)
                              : null
                          }
                          onChange={(value) =>
                            setIncrementalityTest((prev) =>
                              produce(prev, (draft) => {
                                draft.testTo = value
                                  ? moment(value as any).toDate()
                                  : undefined;
                              }),
                            )
                          }
                          minDate={
                            incrementalityTest.testFrom
                              ? moment(incrementalityTest.testFrom)
                              : undefined
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">
                          {CONTROL_PERIOD_LABEL}
                        </Typography>
                        <PeriodDescription>
                          {CONTROL_PERIOD_DESCRIPTION}
                        </PeriodDescription>
                        <DatePicker
                          label="From"
                          value={
                            incrementalityTest.preTestFrom
                              ? moment(incrementalityTest.preTestFrom)
                              : null
                          }
                          onChange={(value) =>
                            setIncrementalityTest((prev) =>
                              produce(prev, (draft) => {
                                draft.preTestFrom = value
                                  ? moment(value as any).toDate()
                                  : undefined;
                              }),
                            )
                          }
                          maxDate={
                            incrementalityTest.preTestTo
                              ? moment(incrementalityTest.preTestTo)
                              : incrementalityTest.testFrom
                                ? moment(incrementalityTest.testFrom).subtract(
                                    1,
                                    "day",
                                  )
                                : undefined
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                        <DatePicker
                          label="To"
                          value={
                            incrementalityTest.preTestTo
                              ? moment(incrementalityTest.preTestTo)
                              : null
                          }
                          onChange={(value) =>
                            setIncrementalityTest((prev) =>
                              produce(prev, (draft) => {
                                draft.preTestTo = value
                                  ? moment(value as any).toDate()
                                  : undefined;
                              }),
                            )
                          }
                          minDate={
                            incrementalityTest.preTestFrom
                              ? moment(incrementalityTest.preTestFrom)
                              : undefined
                          }
                          maxDate={
                            incrementalityTest.testFrom
                              ? moment(incrementalityTest.testFrom).subtract(
                                  1,
                                  "day",
                                )
                              : undefined
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                  {!!dateValidationMessages.length && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {dateValidationMessages[0]}
                    </Alert>
                  )}
                </Stack>
              </GlassCard>

              <GlassCard boxProps={{ p: 3 }}>
                <Stack>
                  <Box mb={2}>
                    <SectionTitle>{REGION_SECTION_TITLE}</SectionTitle>
                    <SectionSubTitle>{REGION_SECTION_SUBTITLE}</SectionSubTitle>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.5}>
                        <IncrementalityTestRegionAutocomplete />
                        <Alert severity="warning">{holdoutNotice}</Alert>

                        <Typography variant="subtitle2">
                          {CONTROL_GROUP_LABEL}
                        </Typography>
                        <RadioGroup
                          value={incrementalityTest.controlGroupType}
                          onChange={(e) => {
                            const nextType = e.target
                              .value as ApiIncrementalityControlGroupType;
                            setIncrementalityTest((prev) =>
                              produce(prev, (draft) => {
                                draft.controlGroupType = nextType;
                                if (
                                  nextType !==
                                  ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED
                                ) {
                                  draft.controlRegions = [];
                                }
                              }),
                            );
                            if (
                              nextType !==
                              ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED
                            ) {
                              setTrendScore(0);
                            }
                          }}
                        >
                          <FormControlLabel
                            sx={{ mb: 1 }}
                            value={
                              ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED
                            }
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="caption" fontWeight={600}>
                                  {AUTO_MATCHED_REGIONS_RECOMMENDED_LABEL}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {buildAutoMatchedControlDescription(
                                    incrementalityTest.testRegionCity,
                                  )}
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            sx={{ mb: 1 }}
                            value={
                              ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_REST_OF_COUNTRY
                            }
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="caption" fontWeight={600}>
                                  Rest of{" "}
                                  {incrementalityTest.testRegionCountry ||
                                    "country"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {buildRestOfCountryControlDescription(
                                    incrementalityTest.testRegionCountry,
                                  )}
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            sx={{ mb: 1 }}
                            value={
                              ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_CUSTOM
                            }
                            control={<Radio />}
                            disabled
                            label={
                              <Box>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                  >
                                    {CUSTOM_REGIONS_LABEL}
                                  </Typography>
                                  <Chip size="small" label={COMING_SOON_LABEL} />
                                </Stack>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {buildCustomRegionsControlDescription(
                                    incrementalityTest.testRegionCity,
                                  )}
                                </Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ height: 300 }}>
                        <IncrementalityTestRegionMap
                          testRegionCoordinates={
                            incrementalityTest.testRegionLat != null &&
                            incrementalityTest.testRegionLon != null
                              ? [
                                  incrementalityTest.testRegionLon,
                                  incrementalityTest.testRegionLat,
                                ]
                              : undefined
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {incrementalityTest.controlGroupType ===
                    ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED && (
                    <Box mt={2}>
                      <IncrementalityControlGroupPreview />
                    </Box>
                  )}
                </Stack>
              </GlassCard>
            </Stack>
          ) : activeStep === 1 ? (
            <IncrementalityMetricsTab />
          ) : activeStep === 2 ? (
            <IncrementalityReviewTab
              onEditTest={() => setActiveStep(0)}
              onEditMetrics={() => setActiveStep(1)}
            />
          ) : (
            <IncrementalityResultsTab />
          )}
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {activeStep > 0 && (
                <Button
                  type="button"
                  variant="text"
                  startIcon={<ArrowBack />}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveStep(Math.max(activeStep - 1, 0));
                  }}
                >
                  Back
                </Button>
              )}
            </Box>
            <Box>
              {activeStep === 0 ? (
                <Stack direction="row" spacing={1}>
                  {showUpdateButton && (
                    <Button
                      type="submit"
                      variant="contained"
                      endIcon={<RocketLaunch />}
                      disabled={isSaving || isLoading}
                    >
                      {isSaving ? "Saving..." : "Update Test"}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant={showUpdateButton ? "outlined" : "contained"}
                    endIcon={<ArrowForward />}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveStep(1);
                    }}
                  >
                    Next: Metrics
                  </Button>
                </Stack>
              ) : activeStep === 1 ? (
                <Stack direction="row" spacing={1}>
                  {showUpdateButton && (
                    <Button
                      type="submit"
                      variant="contained"
                      endIcon={<RocketLaunch />}
                      disabled={isSaving || isLoading}
                    >
                      {isSaving ? "Saving..." : "Update Test"}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant={showUpdateButton ? "outlined" : "contained"}
                    endIcon={<ArrowForward />}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveStep(2);
                    }}
                  >
                    Next: Review
                  </Button>
                </Stack>
              ) : activeStep === 2 ? (
                <Stack direction="row" spacing={1}>
                  {hasResultsTab && (
                    <Button
                      type="button"
                      variant="outlined"
                      endIcon={<ArrowForward />}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveStep(3);
                      }}
                    >
                      View Results
                    </Button>
                  )}
                  {(showCreateButton || showUpdateButton) && (
                    <Button
                      type="submit"
                      variant="contained"
                      endIcon={<RocketLaunch />}
                      disabled={isSaving || isLoading}
                    >
                      {isSaving
                        ? "Saving..."
                        : numericTestId
                          ? "Update Test"
                          : "Create Test"}
                    </Button>
                  )}
                </Stack>
              ) : activeStep === 3 ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant={showUpdateButton ? "outlined" : "contained"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveStep(2);
                    }}
                  >
                    Back to Review
                  </Button>
                  {showUpdateButton && (
                    <Button
                      type="submit"
                      variant="contained"
                      endIcon={<RocketLaunch />}
                      disabled={isSaving || isLoading}
                    >
                      {isSaving ? "Saving..." : "Update Test"}
                    </Button>
                  )}
                </Stack>
              ) : null}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} lg={3.5}>
          <SummaryWrap>
            <IncrementalityTestSummary />
          </SummaryWrap>
        </Grid>
      </Grid>
    </ValidatorForm>
  );
}

function IncrementalitySetupTab() {
  return (
    <IncrementalitySetupProvider>
      <IncrementalitySetupTabContent />
    </IncrementalitySetupProvider>
  );
}

export default IncrementalitySetupTab;
